import { HttpClient, robotoHeaders, type PaginatedResponse } from "../../http";

import {
  type CreateEventTriggerRequest,
  type CreateScheduledTriggerRequest,
  type TriggerEvaluationRecord,
  type TriggerEvaluationSummary,
  type TriggerRecord,
  type TriggerView,
  ScheduledTriggerRecord,
  TriggerType,
  TriggerViewConverter,
  UpdateScheduledTriggerRequest,
  UpdateTriggerRequest,
} from "./TriggerRecord";

type Options = {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
};

export class TriggerService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createEventTrigger(
    request: CreateEventTriggerRequest,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<TriggerView> {
    const requestUrl = this.#httpClient.constructUrl("v1/triggers");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });

    return response
      .json<TriggerRecord>()
      .then((record) => TriggerViewConverter.convertEventTriggerRecord(record));
  }

  public async createScheduledTrigger(
    request: CreateScheduledTriggerRequest,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<TriggerView> {
    const requestUrl = this.#httpClient.constructUrl("v1/triggers/scheduled");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });

    return response
      .json<ScheduledTriggerRecord>()
      .then((record) =>
        TriggerViewConverter.convertScheduledTriggerRecord(record),
      );
  }

  public async deleteTrigger(
    name: string,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "deleteTrigger requires a resource owner ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/triggers/${name}`);
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    await response.throwIfError();
  }

  public async deleteScheduledTrigger(
    triggerId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/triggers/scheduled/id/${triggerId}`,
    );

    const response = await this.#httpClient.delete(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    await response.throwIfError();
  }

  public async getTriggerById(
    triggerId: string,
    options?: Partial<Options>,
  ): Promise<TriggerView> {
    const url = this.#httpClient.constructUrl(
      `v1/triggers/view/id/${triggerId}`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<TriggerView>();
  }

  public async getTriggerByName(
    name: string,
    options?: Partial<Options>,
  ): Promise<TriggerView> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "getTriggerByName requires a resource owner ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/triggers/view/${name}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      signal: options?.abortSignal,
    });

    return response.json<TriggerView>();
  }

  public async getPagedTriggerEvaluationsForDataset(
    datasetId: string,
    options?: Partial<
      Options & {
        limit: number;
        pageToken: string;
      }
    >,
  ): Promise<PaginatedResponse<TriggerEvaluationRecord>> {
    const baseUrl = `v1/triggers/dataset/id/${datasetId}/evaluations`;
    return this.getPagedTriggerEvaluations(baseUrl, options);
  }

  public async getTriggerEvaluationCountForDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<number> {
    const url = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/trigger-evaluations/count`,
    );

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return Number.parseInt(await response.text());
  }

  public async getTriggerEvaluationsForDataset(
    datasetId: string,
    options?: Partial<
      Options & {
        limit: number;
      }
    >,
  ): Promise<TriggerEvaluationRecord[]> {
    const baseUrl = `v1/triggers/dataset/id/${datasetId}/evaluations`;
    const paginatedResponse = await this.getPagedTriggerEvaluations(
      baseUrl,
      options,
    );
    const evaluations = [...paginatedResponse.items];

    let nextPageToken = paginatedResponse.next_token;
    while (nextPageToken) {
      const nextPage = await this.getPagedTriggerEvaluations(baseUrl, {
        ...options,
        pageToken: nextPageToken,
      });
      evaluations.push(...nextPage.items);
      nextPageToken = nextPage.next_token;
    }

    return evaluations;
  }

  public async getPagedTriggerEvaluationsForTrigger(
    triggerName: string,
    options?: Partial<
      Options & {
        limit: number;
        pageToken: string;
        triggerType: TriggerType;
      }
    >,
  ): Promise<PaginatedResponse<TriggerEvaluationRecord>> {
    const baseUrl = `v1/triggers/${triggerName}/evaluations`;
    return this.getPagedTriggerEvaluations(baseUrl, options);
  }

  public async getTriggerEvaluationCountForTrigger(
    triggerId: string,
    options?: Partial<Options>,
  ): Promise<number> {
    const url = this.#httpClient.constructUrl(
      `v1/triggers/id/${triggerId}/evaluations/count`,
    );

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return Number.parseInt(await response.text());
  }

  public async getTriggerEvaluationsForTrigger(
    triggerName: string,
    options?: Partial<
      Options & {
        limit: number;
        triggerType: TriggerType;
      }
    >,
  ): Promise<TriggerEvaluationRecord[]> {
    const baseUrl = `v1/triggers/${triggerName}/evaluations`;
    const paginatedResponse = await this.getPagedTriggerEvaluations(
      baseUrl,
      options,
    );
    const evaluations = [...paginatedResponse.items];

    let nextPageToken = paginatedResponse.next_token;
    while (nextPageToken) {
      const nextPage = await this.getPagedTriggerEvaluations(baseUrl, {
        ...options,
        pageToken: nextPageToken,
      });
      evaluations.push(...nextPage.items);
      nextPageToken = nextPage.next_token;
    }

    return evaluations;
  }

  public async getTriggerEvaluationsSummaryForDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<TriggerEvaluationSummary> {
    const url = this.#httpClient.constructUrl(
      `v1/triggers/dataset/id/${datasetId}/evaluations/summary`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<TriggerEvaluationSummary>();
  }

  public async updateTrigger(
    name: string,
    updates: UpdateTriggerRequest,
    options?: Partial<Options>,
  ): Promise<TriggerRecord> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "updateTrigger requires a resource owner ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/triggers/${name}`);

    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return response.json<TriggerRecord>();
  }

  public async updateScheduledTrigger(
    trigger_id: string,
    updates: UpdateScheduledTriggerRequest,
    options?: Partial<Options>,
  ): Promise<ScheduledTriggerRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/triggers/scheduled/id/${trigger_id}`,
    );

    const response = await this.#httpClient.put(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return response.json<ScheduledTriggerRecord>();
  }

  private async getPagedTriggerEvaluations(
    baseUrl: string,
    options?: Partial<
      Options & {
        limit: number;
        pageToken: string;
        triggerType: TriggerType;
      }
    >,
  ): Promise<PaginatedResponse<TriggerEvaluationRecord>> {
    const { abortSignal, limit, pageToken, resourceOwnerId, triggerType } =
      options ?? {};
    const searchParams = new URLSearchParams();
    if (limit !== undefined) {
      searchParams.append("limit", limit.toString());
    }
    if (pageToken) {
      searchParams.append("page_token", pageToken);
    }
    if (triggerType !== undefined) {
      searchParams.append("trigger_type", triggerType);
    }

    const url = this.#httpClient.constructUrl(baseUrl, searchParams);
    const response = await this.#httpClient.get(url, {
      signal: abortSignal,
      headers: robotoHeaders({ resourceOwnerId }),
    });
    return response.json<PaginatedResponse<TriggerEvaluationRecord>>();
  }
}
